import React from 'react'
import { useSelector } from 'react-redux'
import DisconnectedData from './DisconnectedData.container'
import Pagination from '../../components/Tables/Pagination'
import LoadingContent from '../../components/Placeholders/LoadingContent'
import { usePagination } from '../../components/Tables/Pagination.hooks'
import Table from '../../components/Tables/Table'
import TableCell from '../../components/Tables/TableCell'
import TableRow from '../../components/Tables/TableRow'
import { Paper, Box, Grid } from '@material-ui/core'
import Typography from '../../components/Basics/Typography'

// Formats a percentage (0.0 - 100.0) to one decimal, or a dash when missing.
const formatPercent = value => {
  if (value === null || value === undefined) {
    return '—'
  }
  return `${Number(value).toFixed(1)}%`
}

// Background color for the remaining fuel level (%) when the truck arrived to load.
const remanenteColor = value => {
  if (value === null || value === undefined) {
    return ''
  }
  if (value < 20) {
    return 'rgba(255, 193, 7, 0.25)' // amarillo: llegó con poco margen
  }
  if (value <= 30) {
    return 'rgba(0, 142, 23, 0.18)' // verde: rango óptimo
  }
  return 'rgba(173, 0, 15, 0.15)' // rojo: llegó con demasiado remanente
}

// Minestar reports the remaining fuel in liters; convert to % against total tank capacity to
// compare it with "Remanente" and "COSMOS". FIXME: assumes a single tank capacity for all
// equipment — revisit if trucks with a different capacity start showing up here.
const MINESTAR_TANK_CAPACITY_LITERS = 4922

const formatMinestarPercent = liters => {
  if (liters === null || liters === undefined) {
    return '—'
  }
  return formatPercent((liters / MINESTAR_TANK_CAPACITY_LITERS) * 100)
}

const MAS_CERCANO_LABEL = {
  modelo: 'COSMOS',
  minestar: 'Minestar',
  igual: 'Igual'
}

const formatMasCercano = value => MAS_CERCANO_LABEL[value] || '—'

// 0 = full load, 1 = partial load, null = no ADS data.
const formatCargaParcial = value => {
  if (value === null || value === undefined) {
    return '—'
  }
  return value === 1 ? 'Parcial' : 'Completa'
}

// Background color for the load type: red for partial, green for full.
const cargaParcialColor = value => {
  if (value === null || value === undefined) {
    return ''
  }
  return value === 1
    ? 'rgba(173, 0, 15, 0.15)' // rojo: carga parcial
    : 'rgba(0, 142, 23, 0.18)' // verde: carga completa
}

/**
 * The FuelLevelEstimation's container.
 * Compares the real pre-load fuel level against the COSMOS and Minestar estimates,
 * one row per fuel load event of the last 24h.
 */
const FuelLevelEstimation = () => {
  const { fuelSourceComparison } = useSelector(state => state.countdown)
  const { data: comparisonData = [], loading: comparisonLoading = false, error: comparisonError = false } =
    fuelSourceComparison || {}

  // most recent load first (orpak timestamp, falling back to dispatch)
  const sortedComparison = [...comparisonData].sort((a, b) => {
    const aTs = a.tsOrpak || a.tsDispatch || ''
    const bTs = b.tsOrpak || b.tsDispatch || ''
    return bTs.localeCompare(aTs)
  })

  const { page, totalPages, onChangePage, onGetCurrentPage } = usePagination({
    data: sortedComparison,
    initialPage: 1,
    rowsPerPage: 15
  })

  return (
    <>
      {comparisonLoading && <LoadingContent />}
      {!comparisonLoading && comparisonError && <DisconnectedData />}
      {!comparisonLoading && !comparisonError && (
        <>
          <Box>
            <Typography variant="h2" margin="0 0 20px 0">
              Comparación entre el nivel de combustible real al llegar a cargar y las estimaciones de COSMOS (nuestro
              modelo) y Minestar, para las cargas de las últimas 24 horas.
            </Typography>
            <Paper style={{ overflow: 'auto' }}>
              {sortedComparison.length === 0 ? (
                <>
                  <Table accesibility={{ label: 'Estimación' }}>
                    <TableRow head>
                      <TableCell>Equipo</TableCell>
                      <TableCell>Remanente</TableCell>
                      <TableCell>COSMOS</TableCell>
                      <TableCell>Minestar</TableCell>
                      <TableCell>Más cercano</TableCell>
                      <TableCell>Carga</TableCell>
                    </TableRow>
                  </Table>
                  <Grid container spacing={0} direction="column" alignItems="center">
                    <Typography variant="h1">No hay datos en las últimas 24 horas</Typography>
                  </Grid>
                </>
              ) : (
                <Table accesibility={{ label: 'Estimación' }}>
                  <TableRow head>
                    <TableCell>Equipo</TableCell>
                    <TableCell>Remanente</TableCell>
                    <TableCell>COSMOS</TableCell>
                    <TableCell>Minestar</TableCell>
                    <TableCell>Más cercano</TableCell>
                    <TableCell>Carga</TableCell>
                  </TableRow>

                  {onGetCurrentPage().map((row, index) => {
                    return (
                      <TableRow key={`estimation-${index}`}>
                        <TableCell align="center" minWidth="90px">
                          {row.equipo}
                        </TableCell>
                        <TableCell align="center" minWidth="110px" color={remanenteColor(row.nivelEstanque)}>
                          {formatPercent(row.nivelEstanque)}
                        </TableCell>
                        <TableCell align="center" minWidth="100px">
                          {formatPercent(row.prediccionPct)}
                        </TableCell>
                        <TableCell align="center" minWidth="100px">
                          {formatMinestarPercent(row.remanenteMinestar)}
                        </TableCell>
                        <TableCell align="center" minWidth="120px">
                          {formatMasCercano(row.masCercano)}
                        </TableCell>
                        <TableCell align="center" minWidth="110px" color={cargaParcialColor(row.cargaParcial)}>
                          {formatCargaParcial(row.cargaParcial)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </Table>
              )}
              <Pagination alignment="right" count={totalPages} page={page} onChange={onChangePage} />
            </Paper>
          </Box>
        </>
      )}
    </>
  )
}

export default FuelLevelEstimation
